import React, { useRef, useState, useLayoutEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import useWarrantyClaimsList from './hook/useWarrantyClaimsList';
import WarrantyClaimsTable from './component/WarrantyClaimsTable';
import { useLanguage } from '../../../../contexts/LanguageContext';
import GenericFileExportButton from '../../../../components/common/GenericFileExportButton';
import { useLazyExportWarrantyClaimsQuery } from '../../../../api';
import { WarrantyClaimStatus } from '../../../../utils/warrantyClaimsEnums';

const WarrantyClaims = () => {
    const { th } = useLanguage();

    const tabLabels = [
        { label: th('warrantyClaims.tabs.drafts'), icon: 'pi pi-file-edit', color: 'orange', status: WarrantyClaimStatus.DRAFT },
        { label: th('warrantyClaims.tabs.waiting'), icon: 'pi pi-clock', color: 'blue', status: WarrantyClaimStatus.WAITING },
        { label: th('warrantyClaims.tabs.returned'), icon: 'pi pi-replay', color: 'red', status: WarrantyClaimStatus.RETURNED },
        { label: th('warrantyClaims.tabs.returnedToKarsan'), icon: 'pi pi-arrow-circle-left', color: 'purple', status: WarrantyClaimStatus.RETURNED_TO_KARSAN },
        { label: th('warrantyClaims.tabs.accepted'), icon: 'pi pi-check-circle', color: 'green', status: WarrantyClaimStatus.ACCEPTED },
        { label: th('warrantyClaims.tabs.rejected'), icon: 'pi pi-times-circle', color: 'red', status: WarrantyClaimStatus.REJECTED },
        { label: th('warrantyClaims.tabs.allRequests'), icon: 'pi pi-list', color: 'gray', status: 0 }
    ];

    const {
        apiWarrantyClaims,
        isLoading,
        lazyParams,
        sortField,
        sortOrder,
        onPage,
        onSort,
        activeIndex,
        setActiveIndex,
        activeFilters,
        queryParams,
        handleFilterChange,
        initFilters,
        dealerOptions,
        dealerFilterValue,
        setDealerFilterValue,
        setDealerSearchText,
        dealerCodeOptions,
        dealerCodeFilterValue,
        setDealerCodeFilterValue,
        setDealerCodeSearchText,
        warrantyTypeOptions,
        warrantyTypeFilterValue,
        setWarrantyTypeFilterValue,
        countryOptions,
        tabCounts
    } = useWarrantyClaimsList();

    // Tablar şeride sığmadığında (taşma) scrollable'ı aç, sığdığında kapat.
    const tabViewRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);

    useLayoutEffect(() => {
        const container = tabViewRef.current;
        if (!container) return;

        const checkOverflow = () => {
            const nav = container.querySelector('.p-tabview-nav');
            if (!nav) return;
            // Tabların doğal genişliği kapsayıcıya sığmıyorsa scrollable aç.
            // 1px tolerans, sınırdaki yuvarlama kaynaklı titremeyi önler.
            setIsScrollable(nav.scrollWidth > container.clientWidth + 1);
        };

        checkOverflow();
        const observer = new ResizeObserver(checkOverflow);
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('warrantyClaims.title')}</h1>
                <GenericFileExportButton
                    useExportQuery={useLazyExportWarrantyClaimsQuery}
                    exportParams={queryParams}
                    buttonLabel={th('common.export')}
                    buttonClassName="p-button-outlined"
                    icon="pi pi-file-excel"
                    disabled={isLoading || !apiWarrantyClaims?.totalCount}
                    fileNameExtractor={(result) => result?.fileName}
                    dataExtractor={(result) => result?.fileBase64}
                    contentTypeExtractor={(result) => result?.contentType}
                    exportFormat="base64"
                />
            </div>

            <div ref={tabViewRef}>
                <TabView scrollable={isScrollable} activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="warranty-claims-tabs">
                    {tabLabels.map((tab, index) => {
                        const count = tab.status !== null ? tabCounts?.[tab.status] ?? 0 : 0;
                        return (
                            <TabPanel
                                key={index}
                                header={
                                    <span className="tab-header">
                                        <i className={`${tab.icon} mr-2`}></i>
                                        {tab.label}
                                        {count > 0 && <span className={`ml-2 px-2 py-1 border-round text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-800`}>{count}</span>}
                                    </span>
                                }
                                className="tab-content"
                            >
                                <WarrantyClaimsTable
                                    warrantyClaims={apiWarrantyClaims?.items}
                                    totalCount={apiWarrantyClaims?.totalCount}
                                    lazyParams={lazyParams}
                                    onPage={onPage}
                                    sortField={sortField}
                                    sortOrder={sortOrder}
                                    onSortChange={onSort}
                                    loading={isLoading}
                                    activeIndex={activeIndex}
                                    activeFilters={activeFilters}
                                    onFiltersChange={handleFilterChange}
                                    onResetFilters={initFilters}
                                    dealerOptions={dealerOptions}
                                    dealerFilterValue={dealerFilterValue}
                                    setDealerFilterValue={setDealerFilterValue}
                                    setDealerSearchText={setDealerSearchText}
                                    dealerCodeOptions={dealerCodeOptions}
                                    dealerCodeFilterValue={dealerCodeFilterValue}
                                    setDealerCodeFilterValue={setDealerCodeFilterValue}
                                    setDealerCodeSearchText={setDealerCodeSearchText}
                                    warrantyTypeOptions={warrantyTypeOptions}
                                    warrantyTypeFilterValue={warrantyTypeFilterValue}
                                    setWarrantyTypeFilterValue={setWarrantyTypeFilterValue}
                                    countryOptions={countryOptions}
                                />
                            </TabPanel>
                        );
                    })}
                </TabView>
            </div>
        </div>
    );
};

export default WarrantyClaims;
